import { Resource } from './resource';
import gql from 'graphql-tag';

export class EquipmentResource extends Resource {
  public static async list(start: number, count: number, filters: any) {
    return await this.query(
      {
        query: gql`
          query($filters: Filters!) {
            equipment(filters: $filters) {
              equipment {
                id
                dateOfPurchase
                invoiceNumber
                name
                price
                positionNumber
                deleteDate
                deleteReason
                type {
                  id
                }
                deleted
                annotation
                systemNumber
                systemNumberMask
                systemNumberNumber
                serialNumber
                user {
                  id
                  firstName
                  lastName
                }
                node {
                  id
                  name
                  parentId
                }
                tags {
                  id
                  name
                }
              }
              count
            }
          }
        `,
        variables: { filters: { ...filters, start, count } }
      },
      'equipment'
    );
  }

  public static async findLastMaskNumber(systemNumberMask: string) {
    return await this.query(
      {
        query: gql`
          query($systemNumberMask: String!) {
            findLastMaskNumber(systemNumberMask: $systemNumberMask)
          }
        `,
        variables: { systemNumberMask }
      },
      'findLastMaskNumber'
    );
  }

  public static async add(equipment: any, tags: string[]) {
    return await this.mutation(
      {
        mutation: gql`
          mutation(
            $dateOfPurchase: DateTime
            $invoiceNumber: String
            $name: String
            $price: Float
            $positionNumber: String
            $deleteDate: DateTime
            $deleteReason: String
            $annotation: String
            $deleted: Boolean
            $systemNumber: String
            $systemNumberMask: String
            $systemNumberNumber: Int
            $serialNumber: String
            $type: Float
            $tags: [String!]!
          ) {
            equipmentCreate(
              equipment: {
                dateOfPurchase: $dateOfPurchase
                invoiceNumber: $invoiceNumber
                name: $name
                price: $price
                positionNumber: $positionNumber
                deleteDate: $deleteDate
                deleteReason: $deleteReason
                annotation: $annotation
                deleted: $deleted
                systemNumber: $systemNumber
                systemNumberMask: $systemNumberMask
                systemNumberNumber: $systemNumberNumber
                serialNumber: $serialNumber
                type: $type
              }
              tags: $tags
            ) {
              id
              dateOfPurchase
              invoiceNumber
              name
              price
              positionNumber
              deleteDate
              deleteReason
              type {
                id
              }
              deleted
              annotation
              systemNumber
              systemNumberMask
              systemNumberNumber
              serialNumber
              user {
                id
                firstName
                lastName
              }
            }
          }
        `,
        variables: { ...equipment, tags }
      },
      'equipmentCreate'
    );
  }

  public static async update(id: number, equipment: any, tags: string[]) {
    return await this.mutation({
      mutation: gql`
        mutation(
          $id: Int!
          $dateOfPurchase: DateTime
          $invoiceNumber: String
          $name: String
          $price: Float
          $positionNumber: String
          $systemNumber: String
          $systemNumberMask: String
          $systemNumberNumber: Int
          $serialNumber: String
          $deleted: Boolean
          $annotation: String
          $deleteDate: DateTime
          $deleteReason: String
          $type: Float
          $tags: [String!]!
        ) {
          equipmentUpdate(
            id: $id
            equipment: {
              dateOfPurchase: $dateOfPurchase
              invoiceNumber: $invoiceNumber
              name: $name
              price: $price
              positionNumber: $positionNumber
              deleteDate: $deleteDate
              deleteReason: $deleteReason
              annotation: $annotation
              deleted: $deleted
              systemNumber: $systemNumber
              systemNumberMask: $systemNumberMask
              systemNumberNumber: $systemNumberNumber
              serialNumber: $serialNumber
              type: $type
            }
            tags: $tags
          ) {
            id
            dateOfPurchase
            invoiceNumber
            name
            price
            positionNumber
            deleteDate
            deleteReason
            type {
              id
            }
            deleted
            annotation
            systemNumber
            systemNumberMask
            systemNumberNumber
            serialNumber
            user {
              id
              firstName
              lastName
            }
            tags {
              id
              name
            }
          }
        }
      `,
      variables: { id, ...equipment, tags }
    });
  }

  public static async delete(id: number, start: number, count: number, filters: any, startNext: number) {
    return this.mutation({
      mutation: gql`
        mutation($id: Int!) {
          removeEquipment(id: $id)
        }
      `,
      variables: { id },
      refetchQueries: [
        {
          query: gql`
            query($filters: Filters!) {
              equipment(filters: $filters) {
                equipment {
                  id
                  dateOfPurchase
                  invoiceNumber
                  name
                  price
                  positionNumber
                  deleteDate
                  deleteReason
                  type {
                    id
                  }
                  deleted
                  annotation
                  systemNumber
                  serialNumber
                  user {
                    id
                    firstName
                    lastName
                  }
                  node {
                    id
                    name
                    parentId
                  }
                  tags {
                    id
                    name
                  }
                }
                count
              }
            }
          `,
          variables: { filters: { ...filters, start, count } }
        },
        {
          query: gql`
            query($filters: Filters!) {
              equipment(filters: $filters) {
                equipment {
                  id
                  dateOfPurchase
                  invoiceNumber
                  name
                  price
                  positionNumber
                  deleteDate
                  deleteReason
                  type {
                    id
                  }
                  deleted
                  annotation
                  systemNumber
                  serialNumber
                  user {
                    id
                    firstName
                    lastName
                  }
                  node {
                    id
                    name
                    parentId
                  }
                  tags {
                    id
                    name
                  }
                }
                count
              }
            }
          `,
          variables: { filters: { ...filters, start: startNext, count } }
        }
      ]
    });
  }

  public static async assign(userId: number, equipmentId: number) {
    return await this.mutation(
      {
        mutation: gql`
          mutation($userId: Int, $equipmentId: Int!) {
            equipmentAssign(userId: $userId, equipmentId: $equipmentId) {
              id
              user {
                id
                firstName
                lastName
              }
            }
          }
        `,
        variables: { userId, equipmentId }
      },
      'equipmentAssign'
    );
  }

  public static async removeAssignment(equipmentId: number) {
    return await this.mutation({
      mutation: gql`
        mutation($equipmentId: Int!) {
          equipmentAssign(equipmentId: $equipmentId) {
            id
          }
        }
      `,
      variables: { equipmentId }
    });
  }

  public static async assignStruct(equipmentId: number, nodeId: number) {
    return await this.mutation(
      {
        mutation: gql`
          mutation($equipmentId: Int!, $nodeId: Int) {
            equipmentAssignStruct(equipmentId: $equipmentId, nodeId: $nodeId) {
              id
              node {
                id
                name
                hasChildren
              }
            }
          }
        `,
        variables: { equipmentId, nodeId }
      },
      'equipmentAssignStruct'
    );
  }
}
